import api from '../axios';
import environment from '~/settings/environments';
import { getAccessToken } from '~/utils/storage';

export type ReportMode = 'consolidated' | 'contract';
export type ReportPeriod = 'all' | 'last_year' | 'last_6_months' | 'custom';
export type ReportStatusFilter = 'active' | 'completed' | 'all';
export type ReportDetail = 'summary' | 'full';

export interface ReportFilters {
  period: ReportPeriod;
  periodStart?: string;
  periodEnd?: string;
  status: ReportStatusFilter;
  detail: ReportDetail;
}

export interface PreviewTokenResponse {
  token: string;
  expiresInSeconds: number;
}

const toQuery = (params: Record<string, string | undefined>): string => {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
};

const filtersToParams = (filters: ReportFilters): Record<string, string | undefined> => ({
  period: filters.period,
  periodStart: filters.periodStart,
  periodEnd: filters.periodEnd,
  status: filters.status,
  detail: filters.detail,
});

export async function getReportPreviewTokenRequest(): Promise<PreviewTokenResponse> {
  const { data } = await api.get<PreviewTokenResponse>('/v1/reports/preview-token');
  return data;
}

export function buildReportPreviewUrl(
  mode: ReportMode,
  contractId: string | null,
  filters: ReportFilters,
  token: string,
): string {
  const path =
    mode === 'consolidated'
      ? '/v1/reports/consolidated/preview'
      : `/v1/reports/contracts/${contractId}/preview`;
  return `${environment.api.url}${path}${toQuery({ t: token, ...filtersToParams(filters) })}`;
}

export async function downloadReportPdf(
  mode: ReportMode,
  contractId: string | null,
  filters: ReportFilters,
): Promise<{ blob: Blob; filename: string }> {
  const path =
    mode === 'consolidated'
      ? '/v1/reports/consolidated/pdf'
      : `/v1/reports/contracts/${contractId}/pdf`;
  const params =
    mode === 'consolidated'
      ? filtersToParams(filters)
      : { detail: filters.detail };

  const accessToken = getAccessToken();
  const url = `${environment.api.url}${path}${toQuery(params)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/pdf',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });
  if (!response.ok) {
    throw new Error(`Falha ao gerar PDF (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const dispo = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/i.exec(dispo);
  const fallback =
    mode === 'consolidated'
      ? `paga-agro-relatorio-consolidado-${new Date().toISOString().slice(0, 10)}.pdf`
      : `paga-agro-relatorio-contrato-${new Date().toISOString().slice(0, 10)}.pdf`;
  return { blob, filename: match?.[1] ?? fallback };
}

export async function downloadAndSavePdf(
  mode: ReportMode,
  contractId: string | null,
  filters: ReportFilters,
): Promise<void> {
  const { blob, filename } = await downloadReportPdf(mode, contractId, filters);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
